import { Link, useParams } from "react-router-dom";
import { Badge, Spinner } from "@/components/ui";
import { SECTION_LABELS } from "@/config/constants";
import { getStrapiMedia, blocksToText } from "@/lib/strapi";
import { ROUTES } from "@/routes/paths";
import { useProduct } from "../hooks/useProduct";
import { PlaceOrderForm } from "../components/PlaceOrderForm";

export function ProductDetailPage() {
  const { documentId } = useParams();
  const { data: product, isLoading, error } = useProduct(documentId, {
    populate: { images: true, designer: true },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }
  if (error || !product) {
    return <p className="text-destructive">Product not found.</p>;
  }

  const images = (product.images ?? []).map(getStrapiMedia).filter(Boolean);
  const description = blocksToText(product.description);

  return (
    <div className="grid gap-8 lg:grid-cols-2">
      <div>
        <div className="aspect-square overflow-hidden rounded-lg bg-secondary">
          {images[0] && (
            <img src={images[0]} alt={product.title} className="h-full w-full object-cover" />
          )}
        </div>
        {images.length > 1 && (
          <div className="mt-3 grid grid-cols-4 gap-2">
            {images.slice(1, 5).map((src, i) => (
              <div key={i} className="aspect-square overflow-hidden rounded-md bg-secondary">
                <img src={src} alt="" className="h-full w-full object-cover" />
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          {product.section && (
            <Badge variant="primary">
              {SECTION_LABELS[product.section] ?? product.section}
            </Badge>
          )}
          {product.category && <Badge variant="muted">{product.category}</Badge>}
        </div>
        <h1 className="text-2xl font-bold text-foreground">{product.title}</h1>
        <p className="mt-1 text-2xl font-medium text-accent">${product.price}</p>
        {product.designer?.name && (
          <Link
            to={ROUTES.DESIGNER_DETAIL(product.designer.documentId)}
            className="mt-1 inline-block text-sm text-primary hover:underline"
          >
            by {product.designer.name}
          </Link>
        )}

        {description && (
          <p className="mt-4 whitespace-pre-line text-muted-foreground">{description}</p>
        )}

        <div className="mt-6 rounded-lg border border-border bg-card p-5">
          <h2 className="mb-3 text-lg font-semibold text-foreground">Place an order</h2>
          <PlaceOrderForm product={product} />
        </div>
      </div>
    </div>
  );
}

export default ProductDetailPage;
